import { markRaw, reactive } from 'vue';
import { toast, type ToastT } from 'vue-sonner';
import IdeoToast from './IdeoToast.vue';
import { resolveIdeoToastDuration } from './duration';
import type { IdeoToastId, IdeoToastOptions, IdeoToastVariant } from './types';

export interface IdeoToastClock
{
    deadline?: number;
    remaining: number;
    paused: boolean;
}

const clocks = new Map<IdeoToastId, IdeoToastClock>();
const dismissTimers = new Map<IdeoToastId, ReturnType<typeof setTimeout>>();

function clearDismissTimer(id: IdeoToastId)
{
    const timer = dismissTimers.get(id);

    if (timer !== undefined)
    {
        globalThis.clearTimeout(timer);
        dismissTimers.delete(id);
    }
}

function scheduleDismiss(id: IdeoToastId, deadline?: number, onAutoClose?: IdeoToastOptions['onAutoClose'])
{
    clearDismissTimer(id);

    if (deadline === undefined)
        return;

    dismissTimers.set(id, globalThis.setTimeout(() =>
    {
        dismissTimers.delete(id);
        clocks.delete(id);

        const current = toast.getHistory().find(item => item.id === id && !('dismiss' in item)) as ToastT | undefined;

        if (current)
            onAutoClose?.(current);

        toast.dismiss(id);
    }, Math.max(0, deadline - Date.now())));
}

export function showIdeoToast(variant: IdeoToastVariant, message: string, options: IdeoToastOptions = {}): IdeoToastId
{
    const {
        action,
        description,
        duration,
        onAutoClose,
        onDismiss,
        ...sonnerOptions
    } = options;
    const resolvedDuration = resolveIdeoToastDuration(variant, duration);
    const deadline = Number.isFinite(resolvedDuration) ? Date.now() + resolvedDuration : undefined;
    const clock = reactive<IdeoToastClock>({
        deadline,
        remaining: resolvedDuration,
        paused: options.id !== undefined && clocks.get(options.id)?.paused === true
    });
    const id = toast.custom(markRaw(IdeoToast), {
        ...sonnerOptions,
        duration: Infinity,
        onDismiss: (item) =>
        {
            clearDismissTimer(item.id);
            clocks.delete(item.id);
            onDismiss?.(item);
        },
        unstyled: true,
        componentProps: {
            variant,
            message,
            description,
            action,
            dismissible: options.dismissible !== false,
            duration: deadline === undefined ? undefined : resolvedDuration,
            deadline,
            clock,
            onPauseChange: (paused: boolean) =>
            {
                if (clocks.get(id) !== clock || clock.paused === paused)
                    return;

                clock.paused = paused;

                if (clock.deadline === undefined)
                    return;

                if (paused)
                {
                    clock.remaining = Math.max(0, clock.deadline - Date.now());
                    clearDismissTimer(id);
                }
                else
                {
                    clock.deadline = Date.now() + clock.remaining;
                    scheduleDismiss(id, clock.deadline, onAutoClose);
                }
            }
        }
    });

    clearDismissTimer(id);
    clocks.set(id, clock);

    if (!clock.paused)
        scheduleDismiss(id, deadline, onAutoClose);

    return id;
}

export function closeIdeoToast(id?: IdeoToastId)
{
    if (id === undefined)
    {
        const ids = [...clocks.keys()];

        dismissTimers.forEach(timer => globalThis.clearTimeout(timer));
        dismissTimers.clear();
        clocks.clear();

        for (const toastId of ids)
            toast.dismiss(toastId);

        return;
    }

    clearDismissTimer(id);
    clocks.delete(id);

    return toast.dismiss(id);
}
